<template>
  <div
    v-if="showIndicator"
    class="free-shipping-indicator"
  >
    <div class="free-shipping-indicator__message">
      <component :is="indicatorIcon" />

      <p
        class="free-shipping-indicator__text text-body-xs-regular"
        v-html="progressText"
      />
    </div>

    <div class="free-shipping-indicator__bar-background">
      <div
        class="free-shipping-indicator__bar"
        :style="{ width: `${progress}%` }"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Free shipping indicator (free-shipping-indicator)
 * -----------------------------------------------------------------------------
 * Indicator to show progress to free shipping threshold based on market
 * metafield.
 *
 * @param {Boolean|String|Array} [excluded.value] - Value to test, if boolean
 * then property existing will mean line item is excluded, string and array are
 * used to provide values to test to see if line item should be excluded.
 * @param {Number} freeShippingThreshold - Free shipping threshold price.
 *
 */
import { mapGetters, mapState } from 'vuex'

import IconCollectors from '~icons/general/collectors.svg'
import IconDelivery from '~icons/general/delivery.svg'

export default {
  name: 'FreeShippingIndicator',

  components: {
    IconCollectors,
    IconDelivery,
  },

  props: {
    freeShippingThreshold: {
      type: Number,
      default: 0,
      required: true,
    },
    displayCollectorsClubMembership: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      excluded: {
        property: 'product_type',
        value: ['Emma Bridgewater Membership', 'emma bridgewater membership'],
        condition: 'or',
      },
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      freeMembershipEnabled: (state) => state.collectorsClub.freeMembership.enabled,
      freeMembershipThreshold: (state) => state.collectorsClub.freeMembership.threshold,
      isCollectorsClubMember: (state) => state.collectorsClub.customer.isCollector,
    }),

    ...mapGetters({
      freeMembershipThresholdReached: 'collectorsClub/freeMembershipThresholdReached',
      freeMembershipRemaining: 'collectorsClub/freeMembershipRemaining',
      freeMembershipProgress: 'collectorsClub/freeMembershipProgress',
    }),

    /**
     * Compute if the component should render.
     * @returns {Boolean}
     */
    showIndicator() {
      return Boolean(this.freeShippingThreshold) || this.showCollectorsClubMembershipProgress
    },

    /**
     * Compute icon to display.
     * - Show the collectors icon if only the collectors membership progress is
     *   enabled, otherwise show the delivery icon.
     * @returns {String} component name
     */
    indicatorIcon() {
      const hasOnlyMembershipProgress = this.showCollectorsClubMembershipProgress &&
        !this.freeShippingThreshold

      return hasOnlyMembershipProgress ? 'icon-collectors' : 'icon-delivery'
    },

    /**
     * Compute filtered cart items.
     * @returns {Array}
     */
    cartItems() {
      if (!this.excluded || !this.excluded.property) {
        return this.cart.items
      }

      return this.cart.items.filter((item) => {
        const property = item[this.excluded.property]
        let exclude = false

        switch (typeof this.excluded.value) {

          /**
           * Test to see if property matches excluded value.
           */
          case 'boolean':
            exclude = Boolean(property) === this.excluded.value
            break

          /**
           * If property is array then test to see if string appears in it.
           * - Otherwise test for exact match.
           */
          case 'string':
            exclude = Array.isArray(property)
              ? property.includes(this.excluded.value)
              : property === this.excluded.value
            break

          /**
           * Test property with array based on matching condition.
           * - 'and' - All values must appear in property.
           * - 'or' - A value must appear in property.
           */
          case 'object':
            if (Array.isArray(property)) {
              exclude = this.excluded.condition === 'and'
                ? this.excluded.value.every((value) => property.includes(value))
                : this.excluded.value.some((value) => property.includes(value))

              break
            }

            /**
             * If property is a string then test to see if it appears in array.
             */
            exclude = this.excluded.condition === 'and'
              ? this.excluded.value.every((value) => property === value)
              : this.excluded.value.some((value) => property === value)

            break
        }

        /**
         * Return non-excluded items for filter.
         */
        return !exclude
      })
    },

    /**
     * Compute cart total.
     * @returns {Number} - Total in cents.
     */
    cartTotal() {
      if (!this.excluded || !this.excluded.property) {
        return this.cart.total_price
      }

      let totalPrice = 0

      this.cartItems.forEach((item) => {
        totalPrice += item.line_price
      })

      return totalPrice
    },

    /**
     * Compute progress to threshold.
     * - Caps out at 100(%).
     * @returns {Number} - Percent without % sign.
     */
    progress() {

      /**
       * If only free shipping threshold is set, use free shipping progress.
       */
      if (this.freeShippingThreshold && !this.showCollectorsClubMembershipProgress) {
        return this.freeShippingProgress
      }

      /**
       * If only free membership threshold is set, use free membership progress.
       */
      if (this.showCollectorsClubMembershipProgress && !this.freeShippingThreshold) {
        return this.freeMembershipProgress
      }

      /**
       * If both thresholds are set
       * - Check if both are reached.
       * - Compute progress depending on which threshold is larger than the
       *   other and whether the lower of the two thresholds is reached.
       */
      if (this.freeMembershipThresholdReached && this.freeShippingThresholdReached) {
        return 100
      }

      if (this.freeMembershipThreshold > this.freeShippingThreshold) {
        return this.freeShippingThresholdReached
          ? this.freeMembershipProgress
          : this.freeShippingProgress
      }

      if (this.freeShippingThreshold > this.freeMembershipThreshold) {
        return this.freeMembershipThresholdReached
          ? this.freeShippingProgress
          : this.freeMembershipProgress
      }

      return this.freeShippingProgress
    },

    /**
     * Compute progress text.
     * @returns {String}
     */
    progressText() {

      /**
       * If only Free Shipping is enabled, generate messaging based on whether
       * the free shipping threshold is reached.
       */
      if (this.freeShippingThreshold && !this.showCollectorsClubMembershipProgress) {
        return this.freeShippingThresholdReached
          ? this.$string('cart.free_shipping_indicator.free_shipping_threshold_reached')
          : this.$string(
            'cart.free_shipping_indicator.free_shipping_spend_more_html',
            { replace: { amount: this.$formatMoney(this.freeShippingRemaining) } },
          )
      }

      /**
       * If only Membership is enabled, generate messaging based on whether
       * the free membership threshold is reached.
       */
      if (this.showCollectorsClubMembershipProgress && !this.freeShippingThreshold) {
        return this.freeMembershipThresholdReached
          ? this.$string('cart.free_shipping_indicator.free_membership_threshold_reached')
          : this.$string(
            'cart.free_shipping_indicator.free_membership_spend_more_html',
            { replace: { amount: this.$formatMoney(this.freeMembershipRemaining) } },
          )
      }

      /**
       * If both thresholds are set.
       * - Check if both thresholds are reached
       * - Compute message depending on which threshold is larger than the other
       *   and whether the lower of the two thresholds is reached.
       */
      if (this.freeMembershipThresholdReached && this.freeShippingThresholdReached) {
        return this.$string('cart.free_shipping_indicator.shipping_and_membership_reached')
      }

      if (this.freeMembershipThreshold > this.freeShippingThreshold) {
        if (this.freeShippingThresholdReached) {
          return this.$string(
            'cart.free_shipping_indicator.free_shipping_spend_more_membership_html',
            { replace: { amount: this.$formatMoney(this.freeMembershipRemaining) } },
          )
        }

        return this.$string(
          'cart.free_shipping_indicator.free_shipping_spend_more_html',
          { replace: { amount: this.$formatMoney(this.freeShippingRemaining) } },
        )
      }

      if (this.freeShippingThreshold > this.freeMembershipThreshold) {
        if (this.freeMembershipThresholdReached) {
          return this.$string(
            'cart.free_shipping_indicator.free_membership_spend_more_shipping_html',
            { replace: { amount: this.$formatMoney(this.freeShippingRemaining) } },
          )
        }

        return this.$string(
          'cart.free_shipping_indicator.free_membership_spend_more_html',
          { replace: { amount: this.$formatMoney(this.freeMembershipRemaining) } },
        )
      }

      /**
       * Thresholds for both are the same and threshold not reached.
       */
      return this.$string(
        'cart.free_shipping_indicator.spend_more_html',
        { replace: { amount: this.$formatMoney(this.freeShippingRemaining) } },
      )
    },

    /**
     * Compute free shipping threshold.
     * @returns {Number} Percent without % sign.
     */
    freeShippingProgress() {
      const progress = (this.cartTotal / this.freeShippingThreshold) * 100
      return Math.min(100, progress)
    },

    /**
     * Compute if free shipping threshold is reached.
     * @returns {Boolean}
     */
    freeShippingThresholdReached() {
      return this.cartTotal >= this.freeShippingThreshold
    },

    /**
     * Compute amount remaining to the free shipping threshold.
     * @returns {Number} remaining money in cents.
     */
    freeShippingRemaining() {
      const remaining = this.freeShippingThreshold - this.cartTotal
      return Math.max(0, remaining)
    },

    /**
     * Compute if free collectors club progress should be displayed in the
     * component.
     * @returns {Boolean}
     */
    showCollectorsClubMembershipProgress() {
      return this.displayCollectorsClubMembership &&
        !this.isCollectorsClubMember &&
        this.freeMembershipEnabled
    },
  },
}
</script>

<style lang="scss">
@import './free-shipping-indicator';
</style>
